"use client";

import Link from "next/link";
import { useState } from "react";
import { LoaderCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const initialValues = { firstName: "", lastName: "", email: "", phone: "", message: "", acceptedPrivacy: false, website: "" };

export function ContactForm() {
  const [values, setValues] = useState(initialValues);
  const [status, setStatus] = useState<{ kind: "error" | "success"; message: string } | null>(null);
  const [sending, setSending] = useState(false);

  function update(field: keyof typeof initialValues, value: string | boolean) { setValues((current) => ({ ...current, [field]: value })); }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus(null);
    setSending(true);
    try {
      const response = await fetch("/api/contact", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(values) });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.error || "Le message n’a pas pu être envoyé.");
      setValues(initialValues);
      setStatus({ kind: "success", message: "Merci, votre message a bien été envoyé. Nous vous répondrons dès que possible." });
    } catch (error) {
      setStatus({ kind: "error", message: error instanceof Error ? error.message : "Le message n’a pas pu être envoyé." });
    } finally {
      setSending(false);
    }
  }

  return <form className="contact-form" onSubmit={submit}>
    <FieldGroup>
      <div className="contact-form-names">
        <Field><FieldLabel htmlFor="contact-first-name">Prénom</FieldLabel><Input id="contact-first-name" value={values.firstName} onChange={(event) => update("firstName", event.target.value)} autoComplete="given-name" required /></Field>
        <Field><FieldLabel htmlFor="contact-last-name">Nom <span>facultatif</span></FieldLabel><Input id="contact-last-name" value={values.lastName} onChange={(event) => update("lastName", event.target.value)} autoComplete="family-name" /></Field>
      </div>
      <Field><FieldLabel htmlFor="contact-email">E-mail</FieldLabel><Input id="contact-email" type="email" value={values.email} onChange={(event) => update("email", event.target.value)} autoComplete="email" required /></Field>
      <Field><FieldLabel htmlFor="contact-phone">Téléphone <span>facultatif</span></FieldLabel><Input id="contact-phone" type="tel" value={values.phone} onChange={(event) => update("phone", event.target.value)} autoComplete="tel" /></Field>
      <Field><FieldLabel htmlFor="contact-message">Votre message</FieldLabel><Textarea id="contact-message" value={values.message} onChange={(event) => update("message", event.target.value)} placeholder="Dites-nous ce dont vous avez besoin…" rows={6} required /><FieldDescription>Ne transmettez pas d’informations médicales sensibles.</FieldDescription></Field>
      <Field className="contact-privacy-field" orientation="horizontal"><Checkbox id="contact-privacy" checked={values.acceptedPrivacy} onCheckedChange={(checked) => update("acceptedPrivacy", checked === true)} /><FieldLabel htmlFor="contact-privacy">J’accepte que mes informations soient utilisées pour répondre à ma demande, conformément à la <Link href="/confidentialite">politique de confidentialité</Link>.</FieldLabel></Field>
      <input className="honeypot" tabIndex={-1} autoComplete="off" aria-hidden="true" value={values.website} onChange={(event) => update("website", event.target.value)} />
      {status && (status.kind === "success" ? <p className="form-success" role="status">{status.message}</p> : <FieldError>{status.message}</FieldError>)}
      <Button type="submit" disabled={sending}>{sending ? <LoaderCircle className="spin" data-icon="inline-start" /> : <Send data-icon="inline-start" />} {sending ? "Envoi…" : "Envoyer le message"}</Button>
    </FieldGroup>
  </form>;
}
